import { type ApiSchemas, call } from '@rental/contracts/client'
import { format, formatDate, formatDateTime, formatEuro, formatTime, plural } from '@rental/i18n'
import { Button, cn, Field, Spinner } from '@rental/ui'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { getRouteApi, Link } from '@tanstack/react-router'
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { addDays, type BookingRow } from '../board'
import { BookingItem, StatusBadge } from '../components/BookingItem'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { ROW_STILL } from '../components/Results'
import { StatusChip } from '../components/StatusChip'
import { LoadState } from '../components/states'
import { PageHeading, StaffInput, StaffSelect } from '../components/ui'
import { type AskedTrip, TripDialog } from '../deliveries/TripDialog'
import { TripChips, TripEditButton, TripFacts, useTripWords } from '../deliveries/TripParts'
import { openedByTrips, orderTrips, type TripRow, tripsLead } from '../deliveries/trips'
import { deadlineWords } from '../fines/fines'
import { useLocale } from '../i18n'
import { animate, cssTime, cssToken } from '../motion'
import { problemOf } from '../problems'
import { BookingCode } from '../reports/BookingCode'
import { romeNow } from '../time'
import { ATTENTION, type AttentionKey, attention, cargosTone } from '../today/attention'
import { DayLine } from '../today/DayLine'

const route = getRouteApi('/')

/**
 * Q-79: the board asks the server again every minute while it is on screen.
 * Only an unlocked device renders it (Root), and React Query skips the call
 * while the page is hidden, so a locked or sleeping iPad asks nothing.
 */
const BOARD_REFRESH_MS = 60_000
/** Long enough to be read out; then gone, so nobody comes across an old time later. */
const ANNOUNCEMENT_MS = 7_000

/** A row of an attention list: rules between rows, none under the last. */
const ROW = 'flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-rule py-3 last:border-b-0'

type Board = ApiSchemas['TodayResponse']

/**
 * OPS-6: one business day at the counter, Rome time, from the server's clock
 * (UI-1c): what waits on someone first, then the day at the port, then the
 * pick-ups and returns side by side, then each list that needs a hand.
 */
export function TodayScreen() {
  const { locale, messages: m } = useLocale()
  const search = route.useSearch()
  const navigate = route.useNavigate()
  // Busy and announced only for a refresh someone asked for: the minute's own
  // refresh must neither swallow a tap nor read the time out every minute.
  const [refreshing, setRefreshing] = useState(false)
  const [announcement, setAnnouncement] = useState<{ id: number; text: string } | null>(null)
  useEffect(() => {
    if (!announcement) return
    const timer = setTimeout(() => setAnnouncement(null), ANNOUNCEMENT_MS)
    return () => clearTimeout(timer)
  }, [announcement])
  const board = useQuery({
    queryKey: ['today', search.date ?? null, search.location ?? null],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/today', {
            params: {
              query: {
                ...(search.date ? { date: search.date } : {}),
                ...(search.location ? { locationId: search.location } : {}),
              },
            },
          }),
        ),
      ),
    placeholderData: keepPreviousData,
    refetchInterval: BOARD_REFRESH_MS,
  })
  const data = board.data
  const problem = problemOf(board.error)
  // The day asked for, even while its board is on its way and the last day's
  // stays on screen meanwhile: the field never snaps back, and two taps on an
  // arrow move two days.
  const day = search.date ?? data?.date

  const go = (date: string | undefined) => void navigate({ to: '/', search: { ...search, date } })
  // An arrow moves the day where the eye sees it, beside the title: the new day is said too.
  const step = (days: number) => {
    if (!day) return
    const next = addDays(day, days)
    go(next)
    setAnnouncement((previous) => ({
      id: (previous?.id ?? 0) + 1,
      text: formatDate(locale, next),
    }))
  }

  const refresh = async () => {
    setRefreshing(true)
    try {
      const result = await board.refetch()
      // A failure speaks for itself (ProblemNotice). A new key makes a second
      // tap within the same minute heard again, though its words are the same.
      if (result.isSuccess) {
        const text = format(m.today.updatedAt, { time: formatTime(locale, result.data.now) })
        setAnnouncement((previous) => ({ id: (previous?.id ?? 0) + 1, text }))
      }
    } finally {
      setRefreshing(false)
    }
  }

  const dayField = useRef<HTMLInputElement>(null)
  const groups = useRef<HTMLDivElement>(null)
  useFirstArrival(groups, data !== undefined)
  const { access } = useAccess()
  const tripsFirst = tripsLead(access)
  const readsBookings = can(access, 'bookings.read')

  return (
    <>
      <div className="glass-pane flex flex-col gap-4 rounded-card p-4 sm:p-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-1">
            <PageHeading className="text-3xl">{m.today.title}</PageHeading>
            {day ? <p className="text-lg">{formatDate(locale, day)}</p> : null}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Button
              variant="outline"
              className="rounded-sm px-3"
              aria-label={m.today.previousDay}
              disabled={!day}
              onClick={() => step(-1)}
            >
              <ChevronLeft aria-hidden="true" className="size-6" />
            </Button>
            <Field label={m.today.day}>
              {(control) => (
                <StaffInput
                  {...control}
                  ref={dayField}
                  type="date"
                  value={day ?? ''}
                  onChange={(event) => go(event.target.value || undefined)}
                />
              )}
            </Field>
            <Button
              variant="outline"
              className="rounded-sm px-3"
              aria-label={m.today.nextDay}
              disabled={!day}
              onClick={() => step(1)}
            >
              <ChevronRight aria-hidden="true" className="size-6" />
            </Button>
            {search.date ? (
              <Button
                variant="outline"
                className="rounded-sm"
                onClick={() => {
                  // The button goes with the day it brings back: the focus goes to the day, not
                  // to nothing, and the day is said, as an arrow's is (UI-2g).
                  dayField.current?.focus()
                  go(undefined)
                  setAnnouncement((previous) => ({
                    id: (previous?.id ?? 0) + 1,
                    text: formatDate(locale, romeNow().date),
                  }))
                }}
              >
                {m.today.backToToday}
              </Button>
            ) : null}
            <Field label={m.today.location} hint={m.today.locationHint}>
              {(control) => (
                <StaffSelect
                  {...control}
                  value={search.location ?? ''}
                  onChange={(event) =>
                    void navigate({
                      to: '/',
                      search: { ...search, location: event.target.value || undefined },
                    })
                  }
                >
                  <option value="">{m.today.allLocations}</option>
                  {data?.locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </StaffSelect>
              )}
            </Field>
          </div>
        </div>
        {data ? (
          <div className="flex flex-wrap items-center gap-3 text-sm text-text-muted">
            {/* Another day or place asked for: until it comes, the lists below are the last one's. */}
            {board.isPlaceholderData ? (
              <p className="inline-flex items-center gap-2">
                <Spinner className="size-4" />
                {m.app.loading}
              </p>
            ) : (
              <p>{format(m.today.updatedAt, { time: formatTime(locale, data.now) })}</p>
            )}
            <Button
              variant="link"
              busy={refreshing}
              onClick={() => void refresh()}
              className="text-sm"
            >
              <RefreshCw aria-hidden="true" className="size-4" />
              {m.today.refresh}
            </Button>
          </div>
        ) : null}
      </div>

      <p role="status" className="sr-only">
        {announcement ? <span key={announcement.id}>{announcement.text}</span> : null}
      </p>

      <LoadState data={data} problem={problem}>
        {(data) => {
          // A booking opens for whoever reads bookings, and for a Runner through a trip given to them.
          const byTrips = readsBookings ? null : openedByTrips(data.deliveries, data.now)
          const opens = (code: string) => byTrips === null || byTrips.has(code)
          const trips = <Trips board={data} opens={opens} />
          return (
            // While another day loads, the last day's lists stay and say they are not the new day's yet.
            <div ref={groups} aria-busy={board.isPlaceholderData} className="flex flex-col gap-6">
              <Attention board={data} />
              <DayLine board={data} />
              {tripsFirst ? trips : null}
              <div className="grid gap-6 lg:grid-cols-2">
                <Rows
                  id="today-pickups"
                  title={m.today.pickups}
                  rows={data.pickups}
                  kind="pickup"
                  now={data.now}
                  opens={opens}
                />
                <Rows
                  id="today-returns"
                  title={m.today.returns}
                  rows={data.returns}
                  kind="return"
                  now={data.now}
                  opens={opens}
                />
                {data.overdueReturns.length > 0 ? (
                  <Rows
                    id={ATTENTION.overdue}
                    title={m.today.overdueReturns}
                    rows={data.overdueReturns}
                    kind="return"
                    now={data.now}
                    when="date-time"
                    opens={opens}
                  />
                ) : null}
                {data.overbooked.length > 0 ? (
                  <Rows
                    id={ATTENTION.overbooked}
                    title={m.today.overbooked}
                    rows={data.overbooked}
                    kind="pickup"
                    now={data.now}
                    when="date-time"
                    opens={opens}
                  />
                ) : null}
                {data.missedPickups.length > 0 ? (
                  <Rows
                    id={ATTENTION.missed}
                    title={m.today.missedPickups}
                    rows={data.missedPickups}
                    kind="pickup"
                    now={data.now}
                    when="date-time"
                    opens={opens}
                  />
                ) : null}
              </div>
              {tripsFirst ? null : trips}
              {data.compliance.cargos.length > 0 ? (
                <CargosProblems items={data.compliance.cargos} />
              ) : null}
              {data.compliance.fines.length > 0 ? <FinesDue items={data.compliance.fines} /> : null}
              {data.moneyToReturn.length > 0 ? <MoneyToReturn items={data.moneyToReturn} /> : null}
              {data.depositAlerts.length > 0 ? <DepositAlerts alerts={data.depositAlerts} /> : null}
              <Documents documents={data.expiringDocuments} />
            </div>
          )
        }}
      </LoadState>
    </>
  )
}

/**
 * DESIGN.md, "Motion": the first time Today opens, its groups arrive one after
 * another, a stagger step apart and at most four steps in all; never on the
 * minute's refresh, and not again until the app starts afresh. Asked for less
 * motion, the tokens take no time and everything is there at once.
 */
let arrivedOnce = false
function useFirstArrival(container: RefObject<HTMLElement | null>, ready: boolean) {
  useLayoutEffect(() => {
    const element = container.current
    if (!ready || arrivedOnce || !element) return
    arrivedOnce = true
    const tokens = getComputedStyle(element)
    const duration = cssTime(cssToken(tokens, '--duration-state'))
    if (duration === 0) return
    const step = cssTime(cssToken(tokens, '--stagger-step'))
    const easing = cssToken(tokens, '--ease-glide') || undefined
    const rise = cssToken(tokens, '--space-2')
    Array.from(element.children).forEach((child, index) => {
      animate(
        child,
        [
          { opacity: 0, translate: `0 ${rise}` },
          { opacity: 1, translate: '0 0' },
        ],
        { duration, easing, delay: Math.min(index, 4) * step, fill: 'backwards' },
      )
    })
  }, [container, ready])
}

/**
 * What waits on someone, the worst first, each a link to its list (UI-1c).
 * The link moves the focus to the list's heading, so a keyboard and a screen
 * reader land where the eye does. With links it is a navigation landmark;
 * with nothing waiting it holds no links, so it is a plain region.
 */
function Attention({ board }: { board: Board }) {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const id = useId()
  const items = attention(board, { assignsTrips: can(access, 'deliveries.manage') })
  const titles: Readonly<Record<AttentionKey, string>> = {
    overdue: m.today.overdueReturns,
    overbooked: m.today.overbooked,
    missed: m.today.missedPickups,
    trips: m.trips.toAssign,
    cargos: m.today.cargos,
    fines: m.today.fines,
    money: m.today.moneyToReturn,
    deposits: m.today.depositAlerts,
    documents: m.today.expiringDocuments,
  }
  const Landmark = items.length > 0 ? 'nav' : 'section'
  return (
    <Landmark
      aria-labelledby={id}
      className="glass-pane flex flex-wrap items-center gap-x-4 gap-y-2 rounded-card px-4 py-3 sm:px-6"
    >
      <h2 id={id} className="text-lg">
        {m.today.attention}
      </h2>
      {items.length === 0 ? (
        <StatusChip tone="success">{m.today.attentionClear}</StatusChip>
      ) : (
        <ul className="flex flex-wrap gap-x-2">
          {items.map((item) => (
            <li key={item.key}>
              <a
                href={`#${ATTENTION[item.key]}`}
                onClick={(event) => {
                  event.preventDefault()
                  jumpTo(ATTENTION[item.key])
                }}
                className="inline-flex min-h-touch items-center rounded-control no-underline hover:underline focus-visible:underline"
              >
                <StatusChip tone={item.tone}>
                  {titles[item.key]} <span className="figures font-bold">{item.count}</span>
                </StatusChip>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Landmark>
  )
}

function jumpTo(group: string) {
  const heading = document.getElementById(`${group}-title`)
  heading?.scrollIntoView({ block: 'start' })
  heading?.focus({ preventScroll: true })
}

/** One list on the board: a named region on its own pane, with how many rows it holds. */
function Group({
  id,
  title,
  count,
  children,
}: {
  id: string
  title: string
  count: number
  children: ReactNode
}) {
  return (
    <section
      id={id}
      aria-labelledby={`${id}-title`}
      className="glass-pane flex flex-col gap-2 rounded-card p-4 sm:p-6"
    >
      <h2
        id={`${id}-title`}
        tabIndex={-1}
        className="flex items-baseline gap-2 text-xl focus:outline-none"
      >
        {title} <span className="figures text-base font-medium text-text-muted">{count}</span>
      </h2>
      {children}
    </section>
  )
}

function Rows({
  id,
  title,
  rows,
  kind,
  now,
  when = 'time',
  opens,
}: {
  id: string
  title: string
  rows: readonly BookingRow[]
  kind: 'pickup' | 'return'
  now: string
  when?: 'time' | 'date-time'
  opens: (code: string) => boolean
}) {
  const { messages: m } = useLocale()
  return (
    <Group id={id} title={title} count={rows.length}>
      {rows.length === 0 ? (
        <p className="text-text-muted">{m.today.nothing}</p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <BookingItem
              key={row.code}
              row={row}
              kind={kind}
              now={now}
              when={when}
              opens={opens(row.code)}
            />
          ))}
        </ul>
      )}
    </Group>
  )
}

/**
 * S9 (LOC-5, J8): the day's trips to and from the delivery zones, and those
 * still to be driven from before, the person's own first. The office
 * (deliveries.manage) gives each to someone and writes where the customer is
 * met; whoever drives reads their round here, and opens the booking of a trip
 * given to them while it is theirs to work.
 */
function Trips({ board, opens }: { board: Board; opens: (code: string) => boolean }) {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const manages = can(access, 'deliveries.manage')
  const dialog = useRef<HTMLDialogElement>(null)
  const [asked, setAsked] = useState<AskedTrip | null>(null)
  const [said, setSaid] = useState<number | null>(null)
  const rows = orderTrips(board.deliveries)
  return (
    <Group id={ATTENTION.trips} title={m.trips.title} count={rows.length}>
      {said !== null ? (
        <OutcomeNotice key={said}>
          <p>{m.trips.form.saved}</p>
        </OutcomeNotice>
      ) : null}
      {rows.length === 0 ? (
        <p className="text-text-muted">{m.trips.none}</p>
      ) : (
        <ul className="flex flex-col">
          {rows.map((row) => (
            <TripItem
              key={`${row.booking.code}-${row.leg}`}
              row={row}
              date={board.date}
              opens={opens(row.booking.code)}
              onEdit={
                manages && row.state !== 'done'
                  ? () => setAsked({ trip: row, booking: row.booking })
                  : null
              }
            />
          ))}
        </ul>
      )}
      {manages ? (
        <TripDialog dialogRef={dialog} asked={asked} onSaved={() => setSaid(Date.now())} />
      ) : null}
    </Group>
  )
}

function TripItem({
  row,
  date,
  opens,
  onEdit,
}: {
  row: TripRow
  /** The board's day: a trip due on it shows its time, one left from before its date too. */
  date: string
  opens: boolean
  onEdit: (() => void) | null
}) {
  const { locale, messages: m } = useLocale()
  const tripWords = useTripWords()
  const { booking } = row
  return (
    <li className={cn(ROW_STILL, row.state === 'done' && 'text-text-muted')}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="figures font-bold">
          {romeNow(new Date(row.dueAt)).date === date
            ? formatTime(locale, row.dueAt)
            : formatDateTime(locale, row.dueAt)}
        </span>
        {opens ? (
          <Link
            to="/bookings/$code"
            params={{ code: booking.code }}
            className="figures font-bold touch-target"
            aria-label={`${booking.code}, ${booking.customerName}`}
          >
            {booking.code}
          </Link>
        ) : (
          <span className="figures font-bold">{booking.code}</span>
        )}
        <span className="font-medium">{booking.customerName}</span>
        <div className="ms-auto flex flex-wrap gap-2">
          <TripChips trip={row} />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-muted">
        <span>{tripWords(row.leg, booking)}</span>
        <span className="figures">
          {booking.vehicle ? booking.vehicle.plate : m.today.unassigned}
        </span>
        <TripFacts trip={row} booking={booking} />
      </div>
      {onEdit ? <TripEditButton trip={row} booking={booking} onEdit={onEdit} /> : null}
    </li>
  )
}

/**
 * S3: money owed back to customers, and Stripe refunds that failed or wait for
 * an action. Only the Owner gives money back (Q-75); everyone who takes
 * payments sees what is waiting.
 */
function MoneyToReturn({ items }: { items: Board['moneyToReturn'] }) {
  const { locale, messages: m } = useLocale()
  return (
    <Group id={ATTENTION.money} title={m.today.moneyToReturn} count={items.length}>
      <ul className="flex flex-col">
        {items.map((item) => (
          <li key={item.booking.code} className={ROW}>
            <BookingCode code={item.booking.code} />
            <span>{item.booking.customerName}</span>
            <StatusBadge status={item.booking.status} />
            {item.creditCents > 0 ? (
              <span className="figures font-medium">
                {format(m.today.moneyToReturnAmount, {
                  amount: formatEuro(item.creditCents, locale),
                })}
              </span>
            ) : null}
            {item.refundProblem ? (
              <StatusChip
                tone={
                  // Held by an open dispute: orange, as the open dispute is on the booking.
                  item.refundProblem === 'failed' ? 'danger' : 'warning'
                }
              >
                {item.refundProblem === 'failed'
                  ? m.today.refundFailed
                  : item.refundProblem === 'disputed'
                    ? m.today.refundDisputed
                    : m.today.refundAction}
              </StatusChip>
            ) : null}
          </li>
        ))}
      </ul>
    </Group>
  )
}

/**
 * S6 (CMP-1): today's hand-overs whose CaRGOS report needs the counter. The
 * car does not leave until a receipt covers it: the hand-over screen says what to do.
 */
function CargosProblems({ items }: { items: Board['compliance']['cargos'] }) {
  const { locale, messages: m } = useLocale()
  return (
    <Group id={ATTENTION.cargos} title={m.today.cargos} count={items.length}>
      <ul className="flex flex-col">
        {items.map((item) => (
          <li key={item.booking.code} className={ROW}>
            <Link
              to="/bookings/$code/checkout"
              params={{ code: item.booking.code }}
              className="figures font-bold touch-target"
            >
              {item.booking.code}
            </Link>
            <span>{item.booking.customerName}</span>
            <StatusChip tone={cargosTone(item.status)}>
              {m.today.cargosStatus[item.status]}
            </StatusChip>
            <span className="text-sm text-text-muted">{formatTime(locale, item.at)}</span>
          </li>
        ))}
      </ul>
    </Group>
  )
}

/**
 * S6 (CMP-2), for the holders of fines.manage: fines whose renter is not yet
 * named to the authority, due within a week or overdue. Sent from the fine's page.
 */
function FinesDue({ items }: { items: Board['compliance']['fines'] }) {
  const { locale, messages: m } = useLocale()
  return (
    <Group id={ATTENTION.fines} title={m.today.fines} count={items.length}>
      <ul className="flex flex-col">
        {items.map((item) => (
          <li key={item.id} className={ROW}>
            <Link
              to="/fines/$fineId"
              params={{ fineId: item.id }}
              className="font-bold touch-target"
            >
              {item.noticeNumber}
            </Link>
            <span>{item.authority}</span>
            <span className="figures">{item.plate}</span>
            {item.code ? <span className="figures">{item.code}</span> : null}
            <StatusChip tone={item.state === 'overdue' ? 'danger' : 'warning'}>
              {deadlineWords(
                { on: item.deadlineOn, daysLeft: item.daysLeft, state: item.state },
                locale,
                m,
              )}
            </StatusChip>
          </li>
        ))}
      </ul>
    </Group>
  )
}

/** J6: card holds lapsing within 48 hours (danger within 12), or lapsed while the vehicle is out. */
function DepositAlerts({ alerts }: { alerts: Board['depositAlerts'] }) {
  const { locale, messages: m } = useLocale()
  return (
    <Group id={ATTENTION.deposits} title={m.today.depositAlerts} count={alerts.length}>
      <ul className="flex flex-col">
        {alerts.map((alert) => (
          <li key={alert.code} className={ROW}>
            <BookingCode code={alert.code} />
            <span>{alert.customerName}</span>
            {alert.plate ? <span className="figures">{alert.plate}</span> : null}
            <StatusChip tone={alert.level}>
              {alert.state === 'expired' || !alert.captureBefore
                ? m.today.depositLapsed
                : format(m.today.depositLapses, {
                    date: formatDateTime(locale, alert.captureBefore),
                  })}
            </StatusChip>
          </li>
        ))}
      </ul>
    </Group>
  )
}

/** FLT-3: vehicle documents expiring within 30 days, each plate a way to its vehicle. */
function Documents({ documents }: { documents: Board['expiringDocuments'] }) {
  const { locale, messages: m } = useLocale()
  const { access } = useAccess()
  const fleet = can(access, 'fleet.read')
  return (
    <Group id={ATTENTION.documents} title={m.today.expiringDocuments} count={documents.length}>
      {documents.length === 0 ? (
        <p className="text-text-muted">{m.today.noDocuments}</p>
      ) : (
        <ul className="flex flex-col">
          {documents.map((document) => (
            <li key={`${document.vehicle.id}-${document.kind}`} className={ROW}>
              {fleet ? (
                <Link
                  to="/vehicles/$vehicleId"
                  params={{ vehicleId: document.vehicle.id }}
                  className="figures font-bold touch-target"
                >
                  {document.vehicle.plate}
                </Link>
              ) : (
                <span className="figures font-bold">{document.vehicle.plate}</span>
              )}
              <span>
                {document.vehicle.make} {document.vehicle.model}
              </span>
              <span>{m.vehicleDocuments[document.kind]}</span>
              <span className="figures">{formatDate(locale, document.expiresOn, 'short')}</span>
              {/* FLT-3: alerts at 30 and 7 days; the last week, and anything expired, is urgent. */}
              <StatusChip tone={document.daysLeft <= 7 ? 'danger' : 'warning'}>
                {document.daysLeft > 0
                  ? plural(locale, m.today.expiresIn, document.daysLeft)
                  : document.daysLeft === 0
                    ? m.today.expiresToday
                    : plural(locale, m.today.expiredAgo, -document.daysLeft)}
              </StatusChip>
            </li>
          ))}
        </ul>
      )}
    </Group>
  )
}
